import { Injectable } from '@angular/core';
import { APP_CONFIG, Env } from '../../environment/index';
import { RegisterMapper } from '../register/register.mapper';
import { HttpProvider } from '../http/http';
import { IRegisterResponse } from '../../model/models/register';
import { IRegisterResponseEntity } from '../../model/api/register';
import { FormControlProvider } from '../formbuilder/formcontrol';
import { COUNTRY_SETTINGS, OTP_ACTIONS, RET_VAL } from '../myria/app-variables';
import { RouteProvider } from '../route/route';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { AnalyticsEventType, OtpFailureReason, SegmentLocationNames, AddressSource } from '../../model/types/analytics.const';
import { SegmentProvider } from '../analytics/segment';
import { getSourceAnalyticsData } from '../../providers/analytics/deeplink-source';
import { UtilsProvider } from '../myria/utils-provider';
import { brandings } from '../../environment/branding/brandings';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';
import { TranslateService } from '@ngx-translate/core';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { AccountProvider } from '../../providers/myria/account-provider';
import { LoginProvider } from '../../providers/myria/login-provider';
import { PhoneProvider } from '../phone/phone';
import { CountryDeclarationProvider } from '../country-declaration/country-declaration';
/**
 * @description Recipient Service Provider
 */
@Injectable()
export class RegisterProvider {
  registration = {};
  registrationInLoginToSend = {};
  registrationInLoginToSendChanged = {}; 
  lastLiteRegistrationFlow: boolean = false;
  isPINOnlyRegLoginCountry: boolean = false;
  agentReferralData;  
  countryDeeplinkEventType: number = null;
  static MarketingOptIn: boolean = true;
  private timerIntervals: { [key: string]: any } = {};

  // TODO ! Temporary
  static checkExistingPhoneForSessionFlow = true;

  constructor(private httpProvider: HttpProvider,
              private routeProvider: RouteProvider,              
              private storageProvider: StorageProvider,
              private analytics: SegmentProvider,
              private utilsProvider: UtilsProvider,
              private translate: TranslateService,
              private accountProvider: AccountProvider,
              private formProvider: FormControlProvider,
              private loginProvider: LoginProvider,
              private phoneProvider: PhoneProvider,
              private countryDeclarationProvider: CountryDeclarationProvider
            ) { }

  setCurrentRegistration(registration) {
    this.registration = registration;
  }

  getCurrentRegistration(): any {
    return this.registration;
  }

  setCurrentRegistrationInLoginToSend(registrationInLoginToSend) {
    this.registrationInLoginToSend = registrationInLoginToSend;
  }

  getCurrentRegistrationInLoginToSend(): any {
    return this.registrationInLoginToSend;
  }

  setCurrentRegistrationInLoginToSendChanged(registrationInLoginToSendChanged) {
    this.registrationInLoginToSendChanged = registrationInLoginToSendChanged;
  }

  getCurrentRegistrationInLoginToSendChanged(): any {
    return this.registrationInLoginToSendChanged;
  }

  async getSoftOptinFlowStatus() {
    return await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.SHOW_SOFT_OPTIN) === "1";
  }

  getMarketingOptCheck() {
    return RegisterProvider.MarketingOptIn;
  }

  async sendRequestOptOut() {
    RegisterProvider.MarketingOptIn = false;
    RmtSnackbarComponent.present(this.translate.instant('page.registrationStep1Page.optOutSuccessfulText'));
  }

  static restMarketingOptCheck() {
    RegisterProvider.MarketingOptIn = true;
  }

  showOptOutFlow() {
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: "assets/imgs/opt-out.svg",
        title: this.translate.instant('page.registrationStep1Page.optOutTitle'),
        subtitle: this.translate.instant('page.registrationStep1Page.optOutText'),
        primaryButtonText: this.translate.instant('page.registrationStep1Page.unsubscribeButton'),
        showPrimaryButton: true,
        primaryAction: () => this.sendRequestOptOut(),
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('global.genericCancelButton'),
        secondaryAction: null,
        dismissFromSecondary: true,
      },
      null,
    );
  }

  async restartFlow() {
    if (this.routeProvider.hasPage(this.routeProvider.pages.RegistrationStepUsernamePage))
      this.routeProvider.popTo(this.routeProvider.pages.RegistrationStepUsernamePage);
    else if (this.routeProvider.hasPage(this.routeProvider.pages.RegistrationStep1Page))
      this.routeProvider.popTo(this.routeProvider.pages.RegistrationStep1Page);
    else {
      await this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
      this.routeProvider.push(this.routeProvider.pages.PreLoginPage);
    }
  }

  registerAccount(model: any): Promise<IRegisterResponse> {
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}register`, model, (response: IRegisterResponseEntity) => {
      return RegisterMapper.fromRegisterResponseEntity(response);
    });
  }

  getStates() {
    // IMPORTANT double check that the country hasn't changed so the states are from the correct country
    return this.formProvider.getFieldsRegistration(false, true).then((fields) => {
      const state = fields.filter((field) => (field.id === 'state'));
      if (state && state.length > 0)
        return state[0].options;
      return null;
    });
  }

  getQuestions() {
    return this.formProvider.getFieldsRegistration().then((fields) => {
      const question = fields.filter((field) => (field.id === 'securityQuestion1'));
      if (question && question.length > 0)
        return question[0].options;
      return null;
    });
  }

  checkExistingPhone(phone, captchaSession) {
    const uriPhone = encodeURIComponent(phone);
    return this.httpProvider.get(`${APP_CONFIG.API_URL}api/Account/User/${uriPhone}/IsRegistered`, {}, (result) => {
      result.phone = phone;
      return result;
    }, {
      captchaSession
    });
  }

  checkExistingPhoneForSession(sessionId, phone = null) {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/Account/User/IsRegistered`, {
      sessionId
    }, (result) => {
      if (phone)
        result.phone = phone;
      return result;
    });
  }
    
  getExistingCustomerFields(sessionId) {
    sessionId = encodeURIComponent(sessionId)
    return this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/NewCustomerRequiredFields?SessionId=${sessionId}`).then( res => {
      UtilsProvider.updatePostalCodeRegex(res);
      return res;
    });
  }

  getAgentReferralCustomerFields(token) {
    const headers = {
      AgentReferralToken: token
    };
    return this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/NewCustomerRequiredFields`, {}, null, headers).then( res => {
      UtilsProvider.updatePostalCodeRegex(res);
      return res;
    });
  }

  sendOtp(phoneNumber: string,voice: boolean = false,  action: number, sessionId?: string, captchaSession?: string, channel?: string) {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/OTP/Resource/${phoneNumber}`, {
      'action': action,
      'deliveryType': channel || ((voice) ? SegmentLocationNames.VoicePhoneVerfication : SegmentLocationNames.SmsPhoneVerification),
      'sessionId': sessionId,
    }, null, {
      captchaSession
    });
  };

  async sendSecureOtp(phoneNumber: string,  voice: boolean = false, action: number, sessionId?: string, captchaSession?: string, channel?: string) {
    if (!APP_CONFIG.featureFlags.captcha)
      return await this.sendOtp(phoneNumber,voice, action, sessionId, captchaSession, channel);

    let fallback = false;
    try {
      return await this.httpProvider.post(`${APP_CONFIG.API_URL}api/OTP/SecureResource/${phoneNumber}`, {
        'action': action,
        'deliveryType':  channel || ((voice) ? SegmentLocationNames.VoicePhoneVerfication : SegmentLocationNames.SmsPhoneVerification),
        'sessionId': sessionId,
      }, null, {
        captchaSession
      });
    } catch (ex) {
      if (ex && (ex.status <= 0 || ex.status === 404) && APP_CONFIG.env !== Env.PROD) // in testing the endpoint SecureResource hasn't been deployed everywhere - hence when we get the error of an unknown URL fallback to the obsolete endpoint
        fallback = true;
      else
        throw ex;
    }
    if (fallback)
      return await this.sendOtp(phoneNumber,voice, action, sessionId, captchaSession, channel);
  };

  async sendSigninOtp(pendingSigninToken: string, deliveryType: number, sessionId?: string) {
    try {
      const response = await this.httpProvider.post(`${APP_CONFIG.API_URL}api/OTP/SecureResource/signin`, {
        'Action': OTP_ACTIONS.LOGIN,
        'DeliveryType': deliveryType,
        'SessionID': sessionId || null,
      }, null, {
        'X-Pending-Signin-Token': pendingSigninToken,
      });
      return response;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.CommunicationError, 'sendSigninOtp failed');
      throw ex;
    }
  }

  async validateOtp(phoneNumber: string, code: string, securityCode: string, isFromDeeplink: boolean = false, deliveryType: number) {
    let reqBody = {
      'securityCode': securityCode,
      'referenceValue': phoneNumber,
      'action': OTP_ACTIONS.REGISTRATION,
      'code': code,
      'DeliveryType': deliveryType,
    }
    if(isFromDeeplink){
      const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
      reqBody['DeviceId'] = LoggerProvider.uuid;
      reqBody['DeviceSimCard'] = mfaDeviceToken;
    }
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/OTP/v2/Validate`,reqBody);
  };

  createAccount(account, captchaSession) {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/Account/v2/Create`, account, null, {
      captchaSession,
      ...this.countryDeclarationProvider.getHeaders()
    });
  }

  createLiteAccount(account, captchaSession, oauthProvider) {

      let url = 'api/Account/v2/CreateLite';

      if(oauthProvider){
        url = `api/oauth/account/${oauthProvider}/v2/CreateLite`;
      }

    return this.httpProvider.post(`${APP_CONFIG.API_URL}${url}`, account, null, {
      captchaSession,
      ...this.countryDeclarationProvider.getHeaders()
    });
  }

  customerLink(data: any, sessionId) {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/Customer/CustomerLinking?sessionId=${sessionId}`, data);
  }

  disable() {
    const date = new Date();
    this.storageProvider.set(StorageType.DisabledRegistration, date.getTime().toString());
  }

  async isDisabled() {
    const disabled = await this.storageProvider.get(StorageType.DisabledRegistration);
    if (!disabled)
      return false;
      
    const date = new Date();
    date.setTime(parseInt(disabled));
    const now = new Date();
    const diff = ((now.getTime() - date.getTime()) / 1000) / 60;
    if (diff >= 30)
      return false;
    return true;
  }

  //TODO: remove when remove emailge feature toggle
  isEmailValid(email: string, sessionId: string, captchaSession): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await this.httpProvider.post(APP_CONFIG.API_URL + 'api/Account/EmailValidation', {email: email.trim(), sessionId: sessionId}, null, {
          captchaSession
        });
        resolve(res);
      } catch(ex) {
        resolve(false);
      }
    });
  }

  isReferralCodeValid(referralCode, captchaSession) {
    if (!referralCode || referralCode.trim().length === 0)
      return { RetVal: 0 }; // capture invalid referral codes otherwise we could get errors because the server won't find the endpoint (i.e. ' ')
    return this.httpProvider.get(APP_CONFIG.API_URL + 'api/v1/Moneytransfer/Promotions/' + encodeURIComponent(referralCode) + '/IsValid',{},null, {captchaSession});
  }

  isLiteRegistrationFlow() {
    if (APP_CONFIG.branding.branding == brandings.RIA.branding && this.utilsProvider.getIsLiteRegistrationEnabled())
      return true;
    return false;
  }

  async checkRegistrationTypeChanged() {
    // eventually the country has been changed which results in a reload of the country features and then eventually the registration flow type has been changed
    // hence we check that here again and if that's the case send a registration started event with the new registration flow type
    // i.e: start country is US (full registration) and gets changed to NL (lite registration)
    try {
      let lastLiteRegistrationFlow = this.lastLiteRegistrationFlow;
      await this.utilsProvider.checkIsLiteRegistrationSettingEnabled();
      this.lastLiteRegistrationFlow = this.isLiteRegistrationFlow();
      if (this.lastLiteRegistrationFlow !== lastLiteRegistrationFlow) {
        const country = await this.utilsProvider.getSelectedCountry();
        this.analytics.trackEvent({
          eventName: AnalyticsEventType.RegistrationStarted, analyticsData: {
            location: SegmentLocationNames.CountryConfirmed,
            country: country,
            registrationFlowType: this.utilsProvider.getIsFxDeeplinkShowCountry(this.lastLiteRegistrationFlow ? SegmentLocationNames.LiteRegistrationFlow : SegmentLocationNames.FullRegistrationFlow),
            pilotUser: SegmentProvider.isPilotUser,
            destinationCountry: '',
            ...getSourceAnalyticsData()
          }
        });  
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to check registration type changes');
    }
  }

  mapChoiceSelections(selectedChoice, selectedCategory){
    return [
      {
        "Category": selectedCategory,
        "Answer": {
          "Option": selectedChoice.Option,
          "Value": selectedChoice.Value,
          "Enc": selectedChoice.Enc
        }
      }
    ];
  }

  async setIsPINOnlyRegistration(){
    const isPinOnlyRegisterCountry = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.PIN_ONLY_REGISTRATION);
    this.isPINOnlyRegLoginCountry = (isPinOnlyRegisterCountry == 1) ? true : false;
  }

  getTimeRemaining(endtime: number) {
    const now = Date.now();
    const total = Math.max(0, endtime - now);
    const totalSeconds = Math.floor(total / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { total, hours, minutes, seconds };
  }

  initializeClock(id: string, endtime: number, finishCallback?: () => void, updateCallback?: (text: string) => void) {
    if (!endtime || isNaN(endtime)) {
      if (finishCallback) finishCallback();
      return;
    }

    const clock = document.getElementById(id);

    if (!clock && !updateCallback) return;

    // Clear existing timer if it exists
    if (this.timerIntervals[id]) {
      clearInterval(this.timerIntervals[id]);
    }

    const updateTime = () => {
      let t = this.getTimeRemaining(endtime);

      if (t.total <= 0) {
        clearInterval(this.timerIntervals[id]);
        delete this.timerIntervals[id];
        if (finishCallback) finishCallback();
        return;
      }

      let message: string;
      if (t.hours > 0) {
        message = this.translate.instant('global.mFAContainer.codeResendMessageHour', {
          hr: t.hours,
          mins: t.minutes,
          sec: t.seconds
        });
      } else if (t.minutes > 0) {
        message = this.translate.instant('global.mFAContainer.codeResendMessageMinute', {
          mins: t.minutes,
          sec: t.seconds
        });
      } else {
        message = this.translate.instant('global.mFAContainer.codeResendMessage', { total: t.seconds });
      }

      if (updateCallback) {
        updateCallback(message);
      } else if (clock) {
        clock.innerHTML = message;
      }
    };

    updateTime();
    this.timerIntervals[id] = setInterval(updateTime, 1000);
  }

  stopClock(id: string) {
    if (this.timerIntervals[id]) {
      clearInterval(this.timerIntervals[id]);
      delete this.timerIntervals[id];
    }
  }

  // Common method to check if phone number is already in use across all components
  async checkPhoneNumber(phoneNumber: string, segment: string, captcha: any, country?): Promise<number | null> {
    try {
      if (!captcha) {
        LoggerProvider.error(this, LoggerEvent.CaptchaException, 'Captcha instance is not available');
        return null;
      }

      const captchaSession = await captcha.getToken(segment);
      const check = await this.checkExistingPhone(phoneNumber, captchaSession);

      if (!check) {
        LoggerProvider.error(this, LoggerEvent.UnhandledException, 'No result for check existing phone');
        return null;
      }

      if (check.RetVal !== RET_VAL.PHONE_NUMBER_EXISTS && check.RetVal !== RET_VAL.PHONE_NUMBER_NOT_EXISTS) {
        let error = check.RetMsg;
        if (!error) error = 'Phone number rejected';
        this.handlePhoneNumberRejected(country);
        return;         
      }

      // Return RetVal directly, so components handle logic accordingly
      return check.RetVal;
    } catch (ex) {
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, 'global.myRia.jsonContent.OopsInfo');
      LoggerProvider.exception(this, ex, LoggerEvent.UnhandledException, 'Failed to check existing phone');
      return null;
    }
  }

  handlePhoneInUseForPhoneChange(): void {
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      isWaiting: false,
      imageWithCircle: true,
      image: "assets/icon/yellow-warning-icon.svg",
      title: this.translate.instant('page.phoneVerificationPage.mobilePhoneNumberTakenHeader'),
      subtitle: this.translate.instant('page.phoneVerificationPage.mobilePhoneNumberTakenBody'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.okButton'),
      primaryAction: (routeProv, alertModal) => {
        alertModal.dismissModalAlert();
        this.routeProvider.pop();
      }
    });
  }

  handlePhoneInUseForSignUp(phoneNumber: string): void {
    this.routeProvider.openActionSheetNew(
      this.routeProvider.pages.ModalAlertPage,
      {
        title: this.translate.instant('page.registrationFailedPage.title'),
        subtitle: this.translate.instant('page.registrationFailedPage.accountExistsText'),
        primaryButtonText: this.translate.instant('global.mainNavigation.signInButton'),
        primaryAction: () => {
          const method = phoneNumber ? 'phone' : 'email';
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.LoginStarted,
            analyticsData: {
              location: SegmentProvider.isPilotUser,
              loginMethod: method,
            }
          });
          this.routeProvider.replace(this.routeProvider.pages.SigninPage);
        }
      }
    );
  }

  trackPhoneFailed(analyticsData: any = {}) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.PhoneFailed, analyticsData: {
      location: SegmentLocationNames.Registration,
      country: APP_CONFIG.region.defaultCountryFrom,
      ...analyticsData,
    }});
  }

  handlePhoneNumberRejected(country) {
    this.routeProvider.openActionSheetNew(
      this.routeProvider.pages.ModalAlertPage,
      {
        title: this.translate.instant('global.errorGeneralHeader'),
        subtitle: this.translate.instant('page.phoneVerificationPage.errorTryDifferentNumber'),
        primaryButtonText: this.translate.instant('global.gotItButton'),
      },
    );

    this.analytics.trackEvent({eventName: AnalyticsEventType.OwnershipVerificationFailed, analyticsData: {
      location: 'registration', 
      phoneCountry: country.category,
      country: APP_CONFIG.region.defaultCountryFrom,
      failureReason: OtpFailureReason.UnknownError,
      errorMessage: 'unknown return value',
      registrationFlowType: 'Full'
    }});
    this.trackPhoneFailed({ phoneCountry: country.category, failureReason: 'unknown return value', registrationFlowType: 'Full' });
  }

  async isOriginalCountryAllowedOnly() {
    let originalCountryAllowedOnly = false;
    try {
      const isExternalTester = await this.storageProvider.get(StorageType.IsExternalTester);
      if (APP_CONFIG.env === Env.PROD || APP_CONFIG.checkOriginalCountry) {
        originalCountryAllowedOnly = APP_CONFIG.region.originalCountryAllowedOnly && isExternalTester !== 'true';
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to parse isExternalTester');
    }
    return originalCountryAllowedOnly;
  }

  showAlreadyRegistered() {
    const params = {
      header: this.translate.instant('page.registrationFailedPage.title'),
      texts: [
        this.translate.instant('page.registrationFailedPage.accountExistsText'),
      ],
      primaryButton: this.translate.instant('page.registrationFailedPage.genericLogInButton'),
      isTextCenter: true
    }
    this.routeProvider.openActionSheet(this.routeProvider.pages.ActionsheetInfoPage, params, fromButton => { 
      if(fromButton) 
        this.routeProvider.setRoot(this.routeProvider.pages.SigninPage);
    }, 'rmt-actionsheet-info-modal');

    const register = this.getCurrentRegistration();
    const phoneInUseCountry = (register.phoneCountry) ? register.phoneCountry : APP_CONFIG.region.defaultCountryFrom;
    this.analytics.trackEvent({eventName: AnalyticsEventType.OwnershipVerificationFailed, analyticsData: {
      country: APP_CONFIG.region.defaultCountryFrom,
      phoneCountry: phoneInUseCountry,
      location: SegmentLocationNames.Registration,
      failureReason: 'Phone In Use',
      registrationFlowType: SegmentLocationNames.LiteRegistrationFlow,
    }});
    this.trackPhoneFailed({ phoneCountry: phoneInUseCountry, failureReason: 'Phone In Use', registrationFlowType: SegmentLocationNames.LiteRegistrationFlow });
  }

  showInvalidPhoneAlert() {
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      isWaiting: false,
      imageWithCircle: true,
      image: "assets/icon/yellow-warning-icon.svg",
      title: this.translate.instant('page.phoneVerificationPage.mobilePhoneNumberTakenHeader'),
      subtitle: this.translate.instant('page.phoneVerificationPage.mobilePhoneNumberTakenBody'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.okButton'),
    });
  }

  async startDeeplinkVerifyOtp(token, countryCode, countryIsoCode, userphone, userCountry) {
    var register = this.getCurrentRegistration();
    const formatted = this.phoneProvider.formatPhoneNumber(countryCode, userphone);
    const phoneNumber = formatted.dashed;

    register = {
      ...register,
      phoneNumber: phoneNumber,
      phoneNumberValue: formatted.nationalFormat,
      phoneNumberCountry: countryCode,
      phoneNumberCountryIso: countryIsoCode,
      //token: this.token
    };

    register.UserLogin = {
      SessionId: null,
      DeviceId: null,
      DeviceVerificationId: null,
      Password: null,
      ...register.UserLogin
    };
    register.analyticsData = {
      mobilePhone: formatted.international,
      userCountry: userCountry
    };
    this.setCurrentRegistration(register);
    let otp = null;
    let validOtp = false;
    try {
      otp = await this.sendOtp(phoneNumber,false , OTP_ACTIONS.REGISTRATION);
      if (otp && otp.Value && otp.Result && otp.Result.RetVal === 1) {
        LoggerProvider.info(this, 'Successfully sent OTP');
        validOtp = true;
      } else
        LoggerProvider.info(this, 'Failed to send OTP', otp);
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to send OTP');
      validOtp = false;
    }
    if (!validOtp) {
      this.trackPhoneFailed({ phoneCountry: countryIsoCode, failureReason: 'OTP Send Failed', registrationFlowType: SegmentLocationNames.FxOnlineDeeplinkRegistrationFlow });
      if (this.agentReferralData) {
        await this.routeProvider.push(this.routeProvider.pages.RegistrationStepUsernamePage, {
          nextPage: this.routeProvider.pages.RegistrationStep1Page,
          nextPageParams: {},
          onBack: () => {
            if (this.routeProvider.hasPage(this.routeProvider.pages.RegistrationDeeplinkingWelcomePage))
              this.routeProvider.popTo(this.routeProvider.pages.RegistrationDeeplinkingWelcomePage);
            else
              this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
          }
        });
        this.routeProvider.openActionSheetNew(
          this.routeProvider.pages.ModalAlertPage,
          {
            title: this.translate.instant('page.signInForgotPage.phoneFailedHeader'),
            subtitle: this.translate.instant('page.phoneVerificationPage.errorTryDifferentNumber'),
            primaryButtonText: this.translate.instant('global.gotItButton'),
          },
        );
      } else {
        RmtPopupComponent.showErrorWithLogTranslation('global.mFAContainer.codeSendPhoneFailedMessage');
        this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
      }
      return false;
    }

    const user = {
      // firstname:response.Value.CustomerFirstName,
      register: register,
      userphone: userphone,
      countryCode: countryCode,
      countryIsoCode: countryIsoCode,
      token: token,
      otp: otp
    };

    user.register.token = user.token;
    const formattedPhone = this.phoneProvider.formatPhoneNumber(user.countryCode, user.userphone);
    let params = {
        isValidUrl: true,
        //firstName: user.firstname,
        token: user.token,
        register: user.register,
        maskedPhone: '+' + user.countryCode + ' ' + formattedPhone.nationalFormat,
        phoneNumber: formattedPhone.dashed,
        securityCode: user.otp.Value.SecurityCode,
        otpCode: user.otp.Value.Code,
        // this page will be called directly after OTP to show a loading screen and verify the deeplink
        // then it will take to navigate to the next page depending on the flow (i.e. password created)
        nextPage: this.routeProvider.pages.RegistrationDeeplinkingStepPage,
        waitingOtpTime: user.otp.Value.WaitingOtpTime ? Date.parse(user.otp.Value.WaitingOtpTime) : null,
        registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
        onBack: () => {
          if (this.routeProvider.hasPage(this.routeProvider.pages.RegistrationDeeplinkingWelcomePage))
            this.routeProvider.popTo(this.routeProvider.pages.RegistrationDeeplinkingWelcomePage);
          else
            this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
        },
    }
    LoggerProvider.info(this, 'deeplinkVerified params : ', params);
    this.routeProvider.push(this.routeProvider.pages.RegistrationStepOtpPage, params)

    return false;
  }

  async checkUserCountryAllowed(userCountry, authProvider) {
    LoggerProvider.info(this, 'Check country');
    const selectedCountry = await authProvider.getUserCountry();
    let countryAllowed = true;
    const userCountries = await authProvider.getUserCountries();
    const originalCountryAllowedOnly = await this.isOriginalCountryAllowedOnly();
    if (originalCountryAllowedOnly && userCountry !== selectedCountry) {
      LoggerProvider.info(this, 'Only original country allowed');
      countryAllowed = false;
    } else if (!originalCountryAllowedOnly && userCountries && userCountries.indexOf(userCountry) === -1) {
      LoggerProvider.info(this, `Country ${userCountry} not part of user countries`, userCountries);
      countryAllowed = false;      
    }

    if (!countryAllowed) {
      LoggerProvider.info(this, `Country not allowed ${userCountry} <=> ${selectedCountry}`);
      this.analytics.trackEvent({
        eventName: AnalyticsEventType.CountryConfirmationFailed, analyticsData: {
          initialCountry: userCountry,
          location: SegmentLocationNames.DeepLink,
          registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
        }
      });
      const back = () => {
        this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
      };
      this.utilsProvider.showUnableToRegisterPageBasedOnLocation(false, back, back);
      return false;
    }
    return true;
  }

  public mapDeepLinkAgentReferralData(data) {
    if (!data || !this.hasAgentReferralData(data))
        return null;
    const dataNormalized: any = {};
    for (const key in data) {
      if (key)
        dataNormalized[key.toLowerCase()] = data[key];
    }
    let agentReferralData: any = {
        referralAgentName: (dataNormalized.referralagentname) ? dataNormalized.referralagentname : '',
        referralAgentNumber: (dataNormalized.referralagentnumber) ? dataNormalized.referralagentnumber : '',
        referralAgentId: (dataNormalized.referralagentid) ? dataNormalized.referralagentid : '',
        referralAgentType: (dataNormalized.referralagenttype) ? dataNormalized.referralagenttype : '',
        referralAgentLocationId: (dataNormalized.referralagentlocationid) ? dataNormalized.referralagentlocationid : '',
        referralAgentBranchNo: (dataNormalized.referralagentbranchno) ? dataNormalized.referralagentbranchno : '',
        referralAgentFxOnlineUserName: (dataNormalized.referralagentfxonlineusername) ? dataNormalized.referralagentfxonlineusername : '',
        referralProgram: (dataNormalized.referralprogram) ? dataNormalized.referralprogram : '',
        referralSource: (dataNormalized.referralsource) ? dataNormalized.referralsource : '',
        referralMedium: (dataNormalized.referralmedium) ? dataNormalized.referralmedium : '',
        referralMethod: (dataNormalized.referralmethod) ? dataNormalized.referralmethod : '',
          deeplink: (dataNormalized['+url']) ? dataNormalized['+url'] : '',
          tellerId: (dataNormalized.tellerid) ? dataNormalized.tellerid : '',
          userId: (dataNormalized.userid) ? dataNormalized.userid : '',
    };

    // only fill the marketing data when it's really available otherwise can cause confusion or conflicts
    if (dataNormalized.marketingsmsenabled)
      agentReferralData.marketingSMSEnabled = dataNormalized.marketingsmsenabled;
    if (dataNormalized.marketingemailenabled)
      agentReferralData.marketingEmailEnabled = dataNormalized.marketingemailenabled;
    if (dataNormalized.marketingwhatsappenabled)
      agentReferralData.marketingWhatsappEnabled = dataNormalized.marketingwhatsappenabled;

    return agentReferralData;
  }

  public hasAgentReferralData(data) {
      if (data && data.referralMedium)
        return true;
      if (data && data.ReferralMedium) // old for compatibility !
          return true;
      return false;
  }

  async handleOpenAgentReferral(openAgentReferral, sessionId) {
    this.agentReferralData = {};
    if (openAgentReferral.LinkData)
      this.agentReferralData = this.mapDeepLinkAgentReferralData(openAgentReferral.LinkData);
    else
      LoggerProvider.info(this, 'Missing agent referral link data');
    
    if (openAgentReferral.Token) {
      // open agent referral do not use encrypted tokens
      const deepLinkResult = await this.accountProvider.checkIfDeepLinkPhoneValid(openAgentReferral.Token, {
        SessionId: sessionId
      }, false).toPromise();

      if (deepLinkResult)
        this.applyDeepLinkResult(deepLinkResult.Value);
    } else
      LoggerProvider.info(this, 'Missing agent referral token');
  }

  applyDeepLinkResult(deepLinkResultValue) {
    if (deepLinkResultValue && deepLinkResultValue.CustomerProfile) {
      LoggerProvider.info(this, 'Apply deeplink result', deepLinkResultValue);

      const register = this.getCurrentRegistration();
      if (!register.ProvidedFields)
        register.ProvidedFields = [];
      let fieldMap = {};
      for (const field of register.ProvidedFields) {
        fieldMap[field.FieldId] = field;
      }

      // even if an email has been provided if we already captured an email address that is the one we should use for account creation otherwise it would be confusing
      if (register.emailAddress)
        deepLinkResultValue.CustomerProfile.EmailAddress = register.emailAddress;

      let customerFields = {
        // Step 1
        '8601': deepLinkResultValue.CustomerProfile.FirstName,
        '8701': deepLinkResultValue.CustomerProfile.FamilyName,
        '15201': (deepLinkResultValue.CustomerProfile.BirthInformation && deepLinkResultValue.CustomerProfile.BirthInformation.DateOfBirthISO8601) ? deepLinkResultValue.CustomerProfile.BirthInformation.DateOfBirthISO8601.substr(0,10) : '',
        '18001': deepLinkResultValue.CustomerProfile.EmailAddress,
        // Step 2
        '4701': (deepLinkResultValue.CustomerProfile.Address) ? deepLinkResultValue.CustomerProfile.Address.ProvinceOrState : '',
        '4501': (deepLinkResultValue.CustomerProfile.Address) ? deepLinkResultValue.CustomerProfile.Address.AddressLine1 : '',
        '4801': (deepLinkResultValue.CustomerProfile.Address) ? deepLinkResultValue.CustomerProfile.Address.PostalCode : '',
        '4601': (deepLinkResultValue.CustomerProfile.Address) ? deepLinkResultValue.CustomerProfile.Address.City : '',
      };

      for (const fieldId in customerFields) {
        // the profile can come back with surrounding whitespace which would fail the field regex returned by NewCustomerRequiredFields
        const value = (typeof customerFields[fieldId] === 'string') ? customerFields[fieldId].trim() : customerFields[fieldId];
        if (fieldMap[fieldId])
          fieldMap[fieldId].Value = value;
        else
        register.ProvidedFields.push({ FieldId: fieldId, Value: value });
      }
      if (deepLinkResultValue.CustomerProfile.EmailAddress)
        register.emailAddress = deepLinkResultValue.CustomerProfile.EmailAddress.trim();

      if (!register.analyticsData)
        register.analyticsData = {};
      register.analyticsData['userIsLinked'] = true; // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! double check
      if (!register.analyticsData.userCountry && deepLinkResultValue.CustomerProfile.Address)
        register.analyticsData.userCountry = deepLinkResultValue.CustomerProfile.Address.CountryISO3166A2;
      
      if (deepLinkResultValue.CustomerProfile.Address && !register.addressCreatedTracked) {
        register.addressCreatedTracked = true;
        this.analytics.trackEvent({ eventName: AnalyticsEventType.AddressCreated, analyticsData: {
          location: SegmentLocationNames.Registration,
          country: APP_CONFIG.region.defaultCountryFrom,
          state: deepLinkResultValue.CustomerProfile.Address.ProvinceOrState,
          address: deepLinkResultValue.CustomerProfile.Address.AddressLine1,
          zipCode: deepLinkResultValue.CustomerProfile.Address.PostalCode,
          city: deepLinkResultValue.CustomerProfile.Address.City,
          registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
          addressSource: AddressSource.Manual,
        }});
      }

      this.setCurrentRegistration(register);
      return true;
    }
    return false;
  }

  async checkOpenAgentReferrals() {
    // check if we have an open referral in the storage and then call the BE so that if it's still valid it can link a potential registration to the referral
    try {
      if (!this.agentReferralData) {
        const token = await this.storageProvider.get(StorageType.AgentReferralToken);
        const data = await this.storageProvider.getJson(StorageType.AgentReferralData);
        if (token) {
          await this.accountProvider.checkTokenValidation(token, false , data);
          // set agent referral data only if token is valid
          this.agentReferralData = data
        }
      }
    } catch (ex) {
      // it's just a background check so an error is not a problem (i.e. token expired from QR code)
      LoggerProvider.info(this, 'Check open agent referral failed', ex);
      this.storageProvider.remove(StorageType.AgentReferralToken);
      this.storageProvider.remove(StorageType.AgentReferralData);
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/register/register.ts